import { MANUFACTURER_IDS } from "./manufacturers.js";
import { UNITS } from "./units.js";
import type { Manufacturer } from "../schema/common.js";

export type BannerId = "standard" | "featured" | "manufacturer";

export interface BannerDef {
  id: BannerId;
  name: { de: string; en: string };
  /** Token item for one pull (null: Credits only). */
  token: string | null;
  singleCredits: number;
  tenTokens: number;
  tenCredits: number;
  softPityStart: number;
  softPityStepPct: number;
  hardPity: number;
  aGuaranteeEvery: number;
  /** Free pull cadence: standard daily, featured weekly from VIP 5 (30-units.md R27). */
  free: { period: "day" | "week"; vipLevel: number } | null;
}

export const BANNERS: readonly BannerDef[] = [
  { id: "standard", name: { de: "Standard-Fabrikation", en: "Standard Fabrication" }, token: "itm.fab", singleCredits: 150, tenTokens: 9, tenCredits: 1350, softPityStart: 60, softPityStepPct: 5, hardPity: 80, aGuaranteeEvery: 10, free: { period: "day", vipLevel: 0 } },
  { id: "featured", name: { de: "Spotlight-Fabrikation", en: "Spotlight Fabrication" }, token: "itm.ffab", singleCredits: 250, tenTokens: 9, tenCredits: 2250, softPityStart: 60, softPityStepPct: 5, hardPity: 80, aGuaranteeEvery: 10, free: { period: "week", vipLevel: 5 } },
  { id: "manufacturer", name: { de: "Hersteller-Fabrikation", en: "Manufacturer Fabrication" }, token: null, singleCredits: 200, tenTokens: 0, tenCredits: 1800, softPityStart: 60, softPityStepPct: 5, hardPity: 90, aGuaranteeEvery: 10, free: null },
];

export function bannerById(id: string): BannerDef {
  const b = BANNERS.find((x) => x.id === id);
  if (!b) throw new Error(`unknown banner ${id}`);
  return b;
}

const S_UNITS: readonly string[] = UNITS.filter((u) => u.tier === "S").map((u) => u.id);

/** Featured S unit for a week index (weeks since the epoch week); rotates through every S unit. */
export function featuredUnitFor(weekIndex: number, manufacturer?: Manufacturer): string {
  const pool = manufacturer ? UNITS.filter((u) => u.tier === "S" && u.mfr === manufacturer).map((u) => u.id) : S_UNITS;
  if (!pool.length) throw new Error(`manufacturer has no featured S unit: ${manufacturer}`);
  return pool[((weekIndex % pool.length) + pool.length) % pool.length]!;
}

/**
 * The three S frames a week's spotlight may be aimed at (D100): the week's own frame first, then the
 * next two of the rotation. The player picks one; without a pick the first is the spotlight, as before.
 */
export function spotlightChoicesFor(weekIndex: number, count = 3, manufacturer?: Manufacturer): string[] {
  const pool = manufacturer ? UNITS.filter((u) => u.tier === "S" && u.mfr === manufacturer).length : S_UNITS.length;
  const out: string[] = [];
  for (let i = 0; out.length < Math.min(count, pool) && i < pool; i++) {
    const id = featuredUnitFor(weekIndex + i, manufacturer);
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** Stable existing workshop rotation index from an ISO week key. */
export function fabricationWeekIndex(weekKey: string): number {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekKey);
  if (!match) throw new Error(`invalid fabrication week: ${weekKey}`);
  return Number(match[1]) * 53 + Number(match[2]);
}

/** Manufacturer banner rotation, one per week. */
export function manufacturerFor(weekIndex: number): Manufacturer {
  return MANUFACTURER_IDS[((weekIndex % MANUFACTURER_IDS.length) + MANUFACTURER_IDS.length) % MANUFACTURER_IDS.length]!;
}

/** Cumulative-pull milestones of fabrication events (10 / 30 / 60 / 100 pulls). */
export const PULL_MILESTONES: readonly { pulls: number; items: { itemId: string; qty: number }[] }[] = [
  { pulls: 10, items: [{ itemId: "itm.cdc", qty: 10 }] },
  { pulls: 30, items: [{ itemId: "itm.ufr_b", qty: 20 }] },
  { pulls: 60, items: [{ itemId: "itm.ufr_a", qty: 20 }] },
  { pulls: 100, items: [{ itemId: "itm.ufr_s", qty: 25 }] },
];
